
import React, { useEffect, useState, useRef } from "react";
import Header from "../Components/Header";

const API = {
  products: "https://api.welbeing.in/admin/products/all-products/",
  bestSellers: "https://api.welbeing.in/home/bestseller/",
  add: "https://api.welbeing.in/home/add-bestseller/",
  remove: "https://api.welbeing.in/home/delete-bestseller/",
};

const MAX_BEST_SELLERS = 4;

const readResponse = async (response) => {
  if (!response.ok) {
    throw new Error(`Request failed (${response.status})`);
  }

  try {
    return await response.json();
  } catch {
    throw new Error("The server returned an invalid response.");
  }
};

const BestSeller = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [addedProducts, setAddedProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [adding, setAdding] = useState(false);

  const dropdownRef = useRef(null);
  const addingRef = useRef(false);
  const deletingRef = useRef(false);
  const mountedRef = useRef(false);

  const fetchAllProducts = async () => {
    const response = await fetch(API.products);
    const data = await readResponse(response);

    if (data.status !== "success" || !Array.isArray(data.data)) {
      throw new Error(data.message || "Failed to fetch products.");
    }

    setAllProducts(data.data);
  };

  const fetchBestSellers = async () => {
    const response = await fetch(API.bestSellers);
    const data = await readResponse(response);

    if (data.status !== "success" || !Array.isArray(data.data)) {
      throw new Error(data.message || "Failed to fetch best sellers.");
    }

    setAddedProducts(data.data);
  };

  useEffect(() => {
    mountedRef.current = true;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        fetchAllProducts(),
        fetchBestSellers(),
      ]);

      if (!mountedRef.current) return;

      const failures = results
        .filter((result) => result.status === "rejected")
        .map((result) => result.reason?.message || "Unable to load data.");

      if (failures.length) {
        setError(failures.join(" "));
      }

      setLoading(false);
    };

    loadData();

    return () => {
      mountedRef.current = false;
    };
    // Load initial data once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, []);

  const filteredProducts = allProducts.filter((product) => {
    const term = searchTerm.trim().toLowerCase();

    return (
      product.item_name?.toLowerCase().includes(term) ||
      product.item_code?.toLowerCase().includes(term)
    );
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (addingRef.current) return;

    setError(null);
    setSuccessMessage("");

    if (addedProducts.length >= MAX_BEST_SELLERS) {
      setError(`Maximum ${MAX_BEST_SELLERS} best seller products are allowed.`);
      return;
    }

    if (!selectedProduct) {
      setError("Please select a product.");
      return;
    }

    addingRef.current = true;
    setAdding(true);

    try {
      const response = await fetch(API.add, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          p_id: selectedProduct.p_id,
        }),
      });

      const data = await readResponse(response);

      if (data.status !== "success") {
        throw new Error(
          data.message || "Unable to add this product to Best Sellers."
        );
      }

      setSuccessMessage(
        `"${selectedProduct.item_name}" added to Best Sellers!`
      );

      setSelectedProduct(null);
      setSearchTerm("");
      setDropdownOpen(false);

      try {
        await fetchBestSellers();
      } catch {
        setError(
          "Product was added, but the list could not be refreshed. Please reload the page."
        );
      }
    } catch (err) {
      setError(err.message || "Something went wrong while adding the product.");
    } finally {
      addingRef.current = false;

      if (mountedRef.current) {
        setAdding(false);
      }
    }
  };

  const handleDelete = async (id, name) => {
    if (deletingRef.current || addingRef.current) return;

    deletingRef.current = true;
    setDeletingId(id);
    setError(null);
    setSuccessMessage("");

    try {
      const response = await fetch(API.remove, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      const data = await readResponse(response);

      if (data.status !== "success") {
        throw new Error(data.message || "Failed to remove product.");
      }

      setAddedProducts((previous) =>
        previous.filter((item) => item.id !== id)
      );

      setSuccessMessage(
        `"${name || "Product"}" removed from Best Sellers.`
      );
    } catch (err) {
      setError(err.message || "Something went wrong while removing the product.");
    } finally {
      deletingRef.current = false;

      if (mountedRef.current) {
        setDeletingId(null);
      }
    }
  };

  return (
    <>
      <Header />

      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="alert alert-info">
                  Note: The Best Seller section will be displayed on the
                  website only when a minimum of <b>4 products</b> are added.
                </div>

                {successMessage && (
                  <div
                    className="alert alert-success alert-dismissible fade show"
                    role="alert"
                  >
                    {successMessage}
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setSuccessMessage("")}
                    />
                  </div>
                )}

                {error && (
                  <div
                    className="alert alert-danger alert-dismissible fade show"
                    role="alert"
                  >
                    {error}
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setError(null)}
                    />
                  </div>
                )}

                <div className="mb-3">
                  <h5
                    className="mb-0 fw-bold d-inline-block px-3 py-2 rounded"
                    style={{
                      fontSize: "30px",
                      background:
                        "linear-gradient(135deg, #0d6efd15, #0d6efd08)",
                      borderLeft: "5px solid #0d6efd",
                      color: "#0d6efd",
                      letterSpacing: "0.3px",
                    }}
                  >
                    Add Best Seller Product
                  </h5>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="form-body">
                    <div className="row align-items-end">
                      <div className="col-md-6">
                        <div
                          className="mb-3"
                          ref={dropdownRef}
                          style={{ position: "relative" }}
                        >
                          <label className="form-label">
                            Select Product
                          </label>

                          <div
                            className="form-control d-flex justify-content-between align-items-center"
                            style={{
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            role="button"
                            tabIndex={0}
                            aria-expanded={dropdownOpen}
                            onClick={() => setDropdownOpen((open) => !open)}
                            onKeyDown={(event) => {
                              if (
                                event.key === "Enter" ||
                                event.key === " "
                              ) {
                                event.preventDefault();
                                setDropdownOpen((open) => !open);
                              }
                            }}
                          >
                            {selectedProduct ? (
                              <span>{selectedProduct.item_name}</span>
                            ) : (
                              <span className="text-muted">
                                Search &amp; select a product…
                              </span>
                            )}

                            <i
                              className={`ti ti-chevron-${
                                dropdownOpen ? "up" : "down"
                              } ms-2`}
                            />
                          </div>

                          {dropdownOpen && (
                            <div
                              className="card shadow"
                              style={{
                                position: "absolute",
                                top: "calc(100% + 4px)",
                                left: 0,
                                right: 0,
                                zIndex: 999,
                                border: "1px solid #dee2e6",
                                borderRadius: "8px",
                                overflow: "hidden",
                              }}
                            >
                              <div className="card-body p-0">
                                <div className="p-2 border-bottom">
                                  <input
                                    autoFocus
                                    type="search"
                                    className="form-control form-control-sm"
                                    placeholder="Filter by product name or code…"
                                    value={searchTerm}
                                    onChange={(event) =>
                                      setSearchTerm(event.target.value)
                                    }
                                    onClick={(event) =>
                                      event.stopPropagation()
                                    }
                                  />
                                </div>

                                <ul
                                  className="list-unstyled mb-0"
                                  style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                  }}
                                >
                                  {filteredProducts.length === 0 ? (
                                    <li
                                      className="text-center text-muted py-3"
                                      style={{ fontSize: "0.85rem" }}
                                    >
                                      No products found
                                    </li>
                                  ) : (
                                    filteredProducts.map((product) => (
                                      <li
                                        key={product.p_id}
                                        className={`px-3 py-2 ${
                                          selectedProduct?.p_id ===
                                          product.p_id
                                            ? "bg-light fw-semibold"
                                            : ""
                                        }`}
                                        style={{
                                          cursor: "pointer",
                                          fontSize: "0.9rem",
                                        }}
                                        onClick={() => {
                                          setSelectedProduct(product);
                                          setSearchTerm("");
                                          setDropdownOpen(false);
                                        }}
                                      >
                                        <div>
                                          {selectedProduct?.p_id ===
                                            product.p_id && (
                                            <i className="ti ti-check me-2 text-primary" />
                                          )}

                                          {product.item_name}

                                          {product.item_code && (
                                            <small className="d-block text-muted">
                                              {product.item_code}
                                            </small>
                                          )}
                                        </div>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-2">
                        <div className="mb-3">
                          <button
                            type="submit"
                            className="btn btn-light-primary text-primary font-medium w-100"
                            disabled={
                              !selectedProduct ||
                              adding ||
                              loading ||
                              addedProducts.length >= MAX_BEST_SELLERS
                            }
                          >
                            {adding ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                />
                                Adding…
                              </>
                            ) : (
                              <>
                                <i className="ti ti-plus me-1" /> Add
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>

                <hr />

                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
                  <h5 className="mb-0">Best Seller Products List</h5>

                  <span className="badge bg-dark fs-6">
                    {addedProducts.length}{" "}
                    {addedProducts.length === 1 ? "Product" : "Products"}
                  </span>
                </div>

                <div
                  className="table-responsive"
                  style={{ overflowX: "auto" }}
                >
                  <table className="table border table-striped table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>S.No</th>
                        <th>Product Name</th>
                        <th>Item Code</th>
                        <th className="text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="4" className="text-center py-4">
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Loading products…
                          </td>
                        </tr>
                      ) : addedProducts.length === 0 ? (
                        <tr>
                          <td
                            colSpan="4"
                            className="text-center fw-bold py-3"
                          >
                            No best sellers added yet.
                          </td>
                        </tr>
                      ) : (
                        addedProducts.map((product, index) => (
                          <tr key={product.id}>
                            <td>{index + 1}</td>

                            <td>
                              <div className="d-flex align-items-center">
                                <div>
                                  <h6 className="mb-0">
                                    {product.item_name || product.name || "Unnamed product"}
                                  </h6>
                                </div>
                              </div>
                            </td>

                            <td>{product.item_code || "—"}</td>

                            <td className="text-center">
                              <div className="btn-group">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger"
                                  onClick={() =>
                                    handleDelete(
                                      product.id,
                                      product.item_name || product.name
                                    )
                                  }
                                  disabled={
                                    deletingId !== null ||
                                    adding ||
                                    loading
                                  }
                                  aria-label={`Remove ${
                                    product.item_name || product.name || "product"
                                  }`}
                                >
                                  {deletingId === product.id ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm me-1"
                                        role="status"
                                      />
                                      Removing…
                                    </>
                                  ) : (
                                    <i className="ti ti-trash" />
                                  )}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BestSeller;
